import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline, MdOutlineGroupAdd, MdOutlineGroups2 } from "react-icons/md";

import { useSnack } from "../../../hooks/useSnack";
import classroomServices from "../../../services/classroomServices";
import studentServices from "../../../services/studentServices";
import { assignStudentModalValidationSchema } from "../../../utils/validationSchema";
import Button from "../../Button";
import Modal from "../../Modal";

const AssignStudentModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const [classrooms, setClassrooms] = useState([]);
  const [student, setStudent] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(student) {
      setStudent(student);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const formik = useFormik({
    initialValues: {
      classroom: null,
    },
    validationSchema: assignStudentModalValidationSchema(t),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);

      try {
        const payload = {
          classroom_id: values.classroom,
          students: [student.id],
        };
        const response = await studentServices.assignStudents(payload);
        showSnack(response.message, "success");
        onSubmit();
        resetForm();
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        setIsSubmitting(false);
        showSnack(error, "error");
      }
    },
  });

  const handleCloseModal = () => {
    formik.resetForm(); // Reset the form
    setIsOpen(false); // Close the modal
  };

  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const res = await classroomServices.getAllowedClassrooms(student?.id);
      const formattedClassrooms = res.map((classroom) => ({
        ...classroom,
        label: classroom.name,
        value: classroom.id,
      }));
      setClassrooms(formattedClassrooms);
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchClassrooms();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-[#FF7F50]">
            <MdOutlineGroups2 size={22} />
            {t("assign_to_classroom")}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
          <Button
            className="w-36"
            color="coral"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("assign")}
          />
        </div>
      }
    >
      {/* Modal Body Content */}
      <form onSubmit={formik.handleSubmit} className="space-y-1">
        <Dropdown
          pt={{
            wrapper: "text-sm",
            header: "bg-blue-200 p-2 rounded-t-md",
            input:
              "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
            root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
            panel:
              "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
            item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
          }}
          value={formik.values.classroom}
          onChange={(e) => formik.setFieldValue("classroom", e.value)}
          options={classrooms}
          optionLabel="label"
          optionValue="value"
          placeholder={t("select_classroom")}
          className="w-full"
          loading={loadingClassrooms}
          filter
          emptyFilterMessage={t("no_results_found")}
        />
        {formik.touched.classroom && formik.errors.classroom ? (
          <p className="text-sm text-red-300">{formik.errors.classroom}</p>
        ) : null}
      </form>
    </Modal>
  );
});

export default AssignStudentModal;
